import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import type { RunPromptTestResponse } from '../api/promptTestingApi';

type RunResultError = NonNullable<RunPromptTestResponse['error']>;

export function formatRunResultError(error: RunResultError): string {
  if (isCuratedErrorMessage(error.message)) return error.message;

  switch (error.type) {
    case 'timeout':
      return 'The prompt test timed out. Try a shorter message or try again.';
    case 'rate_limit':
      return 'The model provider is rate limited. Wait a moment and try again.';
    case 'content_filter':
      return 'The model provider blocked this prompt test because of content policy.';
    case 'provider_error':
      return 'The model provider could not complete this prompt test. Try again or check backend logs.';
    default:
      return 'Prompt test failed. Check backend logs and try again.';
  }
}

function isCuratedErrorMessage(message?: string): message is string {
  return (
    message ===
      'You already have two prompt tests running. Wait for one to finish and try again.' ||
    message === 'Your session could not be verified. Refresh the page and sign in again.' ||
    message ===
      'This test persona is no longer available for beta prompt tests. Create a new anonymized persona and try again.' ||
    message ===
      'Beta prompt tests are not fully configured yet. Create a new anonymized persona after setup is complete.'
  );
}

interface ComparisonOutputProps {
  base: RunPromptTestResponse;
  candidate: RunPromptTestResponse;
}

export function ComparisonOutput({ base, candidate }: ComparisonOutputProps) {
  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={600}
        color="text.secondary"
        sx={{ mb: 1, display: 'block' }}
      >
        Comparison Results
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <OutputPanel label="Base" result={base} />
        <OutputPanel label="Candidate" result={candidate} />
      </Box>
    </Box>
  );
}

function OutputPanel({ label, result }: { label: string; result: RunPromptTestResponse }) {
  const hasError = !!result.error;

  const handleCopy = () => {
    navigator.clipboard.writeText(result.output || '');
  };

  return (
    <Box
      sx={{
        flex: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          bgcolor: label === 'Candidate' ? 'primary.light' : 'grey.100',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography variant="caption" fontWeight={600} fontSize={10} sx={{ flex: 1 }}>
          {label.toUpperCase()}
        </Typography>
        {result.latency_ms != null && (
          <Chip
            label={`${result.latency_ms}ms`}
            size="small"
            variant="outlined"
            sx={{ height: 18, '& .MuiChip-label': { fontSize: 9 } }}
          />
        )}
        {result.cost_usd != null && (
          <Chip
            label={`$${result.cost_usd.toFixed(4)}`}
            size="small"
            variant="outlined"
            sx={{ height: 18, '& .MuiChip-label': { fontSize: 9 } }}
          />
        )}
        <Chip
          label={result.has_document_context ? `${result.document_count} docs` : 'quick'}
          size="small"
          variant="outlined"
          sx={{ height: 18, '& .MuiChip-label': { fontSize: 9 } }}
        />
        <Tooltip title="Copy output">
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
            <ContentCopyIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5, maxHeight: 500, overflow: 'auto' }}>
        {hasError ? (
          <Alert severity="error" sx={{ fontSize: 11 }}>
            {formatRunResultError(result.error!)}
          </Alert>
        ) : (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11, lineHeight: 1.6 }}
          >
            {result.output || '(empty output)'}
          </Typography>
        )}

        {result.resolved_prompts && result.resolved_prompts.length > 0 && (
          <Accordion disableGutters sx={{ mt: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
              <Typography variant="caption" fontWeight={600}>
                Resolved prompts ({result.resolved_prompts.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {result.resolved_prompts.map((prompt) => (
                <Box key={`${prompt.name}-${prompt.resolved_id}`} sx={{ mb: 0.75 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                    {prompt.name} v{prompt.version}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {prompt.resolved_id}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
}
